import { FC, PropsWithChildren } from 'react';
import { useLocation } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import {
  AutoExposureIcon,
  resourceIconMap,
  resourceNameMap,
  ResourceType,
} from '@dbt-labs/dbt-dag';
import {
  Icon,
  Link,
  NotificationBanner,
  type Ryecon,
  Tooltip,
} from '@dbt-labs/sourdough';

import { AutoExposureBiProvider, ModelVersion } from '@/__generated__/graphql';
import { DataPlatformChip } from '@/components/DataPlatformChip';
import { DeprecationBanner } from '@/components/DeprecationBanner';
import { PageHeading } from '@/components/PageHeading';
import { XPIndicatorFn } from '@/components/XPIndicator';
import { AssetBreadcrumbs } from '@/pages/account/catalog/details/components/AssetBreadcrumbs';
import { NavigationResourceType } from '@/panel/ResourcesFilter/selectedResource';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { HeaderButtons } from './HeaderButtons';
import { ModelVersionDropdown } from './ModelNameWithAccessIcon';
import { ResourceBreadcrumbs } from './ResourceBreadcrumbs';

export const millisToDays = (millis: number) => {
  return Math.floor(millis / (1000 * 60 * 60 * 24));
};

export type TextIconPair =
  | {
      ryecon: Ryecon;
      text: string;
      Contents?: undefined;
      key?: undefined;
      tooltip?: string;
      href?: string;
    }
  | {
      ryecon: Ryecon;
      text?: undefined;
      Contents: FC;
      key: string;
      tooltip?: string;
      href?: string;
    };

export const getResourceTypeHeaderIcon = (resourceType: ResourceType): TextIconPair => {
  return {
    ryecon: resourceIconMap[resourceType],
    text: resourceNameMap[resourceType],
  };
};

type ResourceDetailsHeaderParams = PropsWithChildren<{}> & {
  title: string;
  uniqueId?: string;
  headerIcons: TextIconPair[];
  willHaveHeaderIcons?: boolean;
  exposureUrl?: string | null;
  packageName: string | null | undefined;
  files: (string | null | undefined)[];
  resourceType: NavigationResourceType | 'package';
  sourceName?: string | null;
  name: string | null | undefined;
  updatedDate: Date | null | undefined;
  deprecationDate?: string | null;
  autoExposureBiProvider?: AutoExposureBiProvider | null;
  rightAdornment?: React.JSX.Element;
  versions?: ModelVersion[];
};

export const ResourceDetailsHeader: FC<ResourceDetailsHeaderParams> = ({
  title,
  uniqueId,
  children,
  headerIcons,
  exposureUrl,
  packageName,
  files,
  resourceType,
  sourceName,
  name,
  willHaveHeaderIcons = true,
  updatedDate,
  deprecationDate,
  autoExposureBiProvider,
  rightAdornment = null,
  versions,
}) => {
  XPIndicatorFn({});
  const location = useLocation();
  const isInCatalogView = location.pathname.includes('/catalog/');
  const parsedUniqueId = uniqueId ? parseUniqueId(uniqueId) : undefined;

  if (!name) return null;

  const shouldShowVersions = versions && (versions?.length ?? 0) > 1;

  const isStale = updatedDate
    ? millisToDays(Date.now() - updatedDate.getTime()) > 30
    : false;

  return (
    <>
      {isInCatalogView ? (
        <AssetBreadcrumbs />
      ) : (
        <ResourceBreadcrumbs
          resourceType={resourceType}
          name={name}
          sourceName={sourceName}
        />
      )}
      {deprecationDate && <DeprecationBanner deprecationDate={deprecationDate} />}
      {isStale && (
        <div className="flex-grow-0">
          <NotificationBanner
            isGlobal={false}
            notification={{
              id: 'staleState',
              type: 'warning',
              header: 'Stale state',
              helperText: (
                <p>
                  This {resourceType}'s metadata is more than a month old. In order to
                  view its latest{' '}
                  <Link
                    isInternal={false}
                    to="https://docs.getdbt.com/docs/collaborate/explore-projects#generate-metadata"
                    onClick={(e) => e.stopPropagation()}
                    shouldOpenNewTab
                    colorVariant="warning"
                  >
                    metadata
                  </Link>
                  , please run the {resourceType} to update it.
                </p>
              ),
            }}
          />
        </div>
      )}
      <div className="mb-2 mt-4 flex">
        {/*
          When children are provided, ensure it has a height of 10rem to prevent
          page layout differences between pages
        */}
        <span className={`min-w-0 flex-1 ${!shouldShowVersions ? 'h-10' : ''}`}>
          {children ? (
            children
          ) : (
            <div
              className={`flex items-center whitespace-nowrap ${!shouldShowVersions ? 'overflow-hidden truncate' : ''}`}
            >
              {!!autoExposureBiProvider && (
                <AutoExposureIcon
                  biProvider={autoExposureBiProvider}
                  className="h-5 pr-1.5"
                />
              )}
              <PageHeading
                className="flex w-min items-center gap-2"
                additional={{
                  left: <DataPlatformChip platform="dbt" size="lg" bordered={false} />,
                  right: rightAdornment,
                }}
                shouldTruncate={!shouldShowVersions}
              >
                {shouldShowVersions && parsedUniqueId ? (
                  <ModelVersionDropdown uniqueId={parsedUniqueId} versions={versions} />
                ) : (
                  title
                )}
              </PageHeading>
            </div>
          )}
        </span>
        <HeaderButtons
          uniqueId={uniqueId}
          resourceType={resourceType}
          name={name}
          packageName={packageName}
          files={files}
          exposureUrl={exposureUrl}
        />
      </div>
      {headerIcons.length > 0 && (
        <div
          className={twJoin(
            'mb-4 flex items-center gap-4 duration-300 motion-reduce:duration-0',
            willHaveHeaderIcons && 'min-h-[26px]',
          )}
        >
          {headerIcons.map(({ key, ...props }: TextIconPair) => {
            if (props.href) {
              return (
                <Link key={props.text ?? key} isInternal={false} to={props.href}>
                  <HeaderIconItem {...props} />
                </Link>
              );
            }
            return <HeaderIconItem key={props.text ?? key} {...props} />;
          })}
        </div>
      )}
    </>
  );
};

export const HeaderIconItem = ({
  ryecon,
  text,
  Contents,
  tooltip,
}: Omit<TextIconPair, 'key'>) => {
  return (
    <span className="flex items-center">
      <Tooltip content={tooltip} className="flex items-center">
        <span className="font-caption space-x-1 space-y-2 text-fgDecorative">
          <Icon ryecon={ryecon} className="align-middle" />
          <span className="align-middle">
            {Contents && <Contents />}
            {!Contents && text}
          </span>
        </span>
      </Tooltip>
    </span>
  );
};
